import { CellContext } from '@tanstack/react-table';
import clsx from 'clsx';
import React, { useCallback, useRef, useState } from 'react';
import { Control, useFormContext, useWatch } from 'react-hook-form';
import { PosTextArea } from 'src/core/POS/PosTextArea';
import { Popover } from 'src/core/ui';
import * as styles from 'src/tables/SpreadsheetTable/SpreadsheetTable.css';
import { posChangedField } from 'src/utils/posFieldUtils';
import { PurchaseTicketsInput } from 'src/utils/purchaseUtils';
import { TicketGroupInput } from 'src/WebApiController';

function TicketGroupInputPrivateNoteComponent({
  row: { index: rowIndex },
  control,
}: CellContext<TicketGroupInput, unknown> & {
  control: Control<PurchaseTicketsInput, any>;
}) {
  const { setValue } = useFormContext<PurchaseTicketsInput>();
  const [isOpen, setIsOpen] = useState(false);
  const localValueRef = useRef('');

  const internalNotes = useWatch({
    control,
    name: `ticketGroups.${rowIndex}.internalNotes`,
  });

  const currentValue = internalNotes?.value ?? '';

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        localValueRef.current = currentValue;
      } else {
        // Save on close
        if (localValueRef.current !== currentValue) {
          setValue(
            `ticketGroups.${rowIndex}.internalNotes`,
            posChangedField(localValueRef.current || null)
          );
        }
      }
      setIsOpen(open);
    },
    [currentValue, setValue, rowIndex]
  );

  return (
    <div className={styles.listingNotesCellWrapper}>
      <Popover.Root open={isOpen} onOpenChange={handleOpenChange}>
        <Popover.Trigger asChild>
          <div
            className={clsx(
              styles.tableCell[currentValue ? 'active' : 'disabled'],
              styles.privateNoteTrigger
            )}
          >
            {currentValue || ''}
          </div>
        </Popover.Trigger>
        <Popover.Content
          side="bottom"
          align="start"
          sideOffset={-40}
          className={styles.privateNotePopoverContent}
        >
          <PosTextArea
            defaultValue={currentValue}
            onChange={(e) => {
              localValueRef.current = e.target.value;
            }}
            onKeyDown={(e) => {
              // Stop propagation so spreadsheet copy/paste doesn't intercept
              if (
                (e.metaKey || e.ctrlKey) &&
                (e.key === 'c' || e.key === 'v')
              ) {
                e.stopPropagation();
              }
            }}
            rows={4}
            maxLength={4000}
            className={styles.privateNoteTextArea}
            rootProps={{
              className: styles.privateNoteTextAreaRoot,
            }}
          />
        </Popover.Content>
      </Popover.Root>
    </div>
  );
}

export const TicketGroupInputPrivateNote = React.memo(
  TicketGroupInputPrivateNoteComponent
) as (
  props: CellContext<TicketGroupInput, unknown> & {
    control: Control<PurchaseTicketsInput, any>;
  }
) => JSX.Element;
